'use client';
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import CustomToast from '../CustomToast';
import useAuth from '@/hooks/useAuth';
import { useQuery } from 'react-query';
import { GetDocument } from '@/services/api/personalize/GetDocuments';
import CoversSelection from './CoversSelection';
import GetCovers from '@/services/api/personalize/GetCovers';
import { GetSettings } from '@/services/api/settings';
import { useCartDispatch } from '@/contexts/CartContext';
import { addItem, updateItem } from 'CartActions';
import { event } from 'nextjs-google-analytics';
import * as fbq from '@/services/fbpixel';
import { usePersonalizeModal } from '@/contexts/PersonalizeModalContext';
import PackagingSelection from './PackagingSelection';
import PersonalizationFooter from './PersonalizeModalFooter';
import { GetDedicationImageURL, GetDedicationImage } from '@/services/api/personalize/DedicationImage';
import axios from 'axios';
import GlobalLoader from '../GlobalLoader';
import ConfirmationModal from './PersonalizeModal/ConfirmationModal';
import BookViewMenu from './PersonalizeModal/BookViewMenu';
import PersonalizeHeader from './PersonalizeModal/PersonalizeHeader';
import InfoStep from './PersonalizeModal/InfoStep';
import ReviewStep from './PersonalizeModal/ReviewStep';
import EditStep from './PersonalizeModal/EditStep';
import PersonalizeModals from './PersonalizeModal/PersonalizeModals';
import { useTranslation } from 'next-i18next';
import useUserPreferences from '@/hooks/useUserPreferences';
import { usePersonalizeForm } from '@/hooks/usePersonalizeForm';
import { usePriceCalculation } from '@/hooks/usePriceCalculation';
import { savePersonalization, loadPersonalizationArray } from '@/utils/personalizationStorage';
import { parseMarketScope } from '@/utils/parseMarketScope';

const CURRENCY_SYMBOLS: Record<string, string> = {
  ILS: '₪', USD: '$', EUR: '€', GBP: '£', CAD: '$',
  SAR: '﷼', AED: 'د.إ', OMR: '﷼', BHD: '.د.ب', QAR: 'ر.ق', KWD: 'د.ك',
};

export default function PersonalizeModal({
  setPersonalizeModal,
  product,
  inputs,
  editFromCart,
}: {
  setPersonalizeModal: any;
  product: any;
  inputs: string[];
  editFromCart?: any;
}) {
  const router = useRouter();
  const { t } = useTranslation('personalizeModal');
  const { t: tAlerts } = useTranslation('alerts');
  const { locale } = useRouter();
  const { productData, updateProductData, updateDedicationImage, setProductRtl } = usePersonalizeModal();
  const { user } = useAuth('none');
  const preferences = useUserPreferences();
  const cartDispatch = useCartDispatch();

  // ─── Queries ────────────────────────────────────────────────────────────────
  const { data: settings, isLoading: isSettingsLoading } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  );

  // ─── Currency ───────────────────────────────────────────────────────────────
  const [userCurrency, setUserCurrency] = useState('ILS');
  const [priceSymbol, setPriceSymbol] = useState('₪');

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user-locale-choice');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.currency) {
          setUserCurrency(parsed.currency);
          setPriceSymbol(CURRENCY_SYMBOLS[parsed.currency] || parsed.currency);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // ─── Covers (needs userCurrency) ────────────────────────────────────────────
  const product_id = productData?.isEdit === '1' ? product?.product_id : product?.id;
  const { data: covers } = useQuery(['covers', product_id, userCurrency], GetCovers);

  // ─── Packaging availability ──────────────────────────────────────────────────
  const [showPackagingOption, setShowPackagingOption] = useState(() => {
    if (productData?.isEdit === '1') {
      if (product?.packaging?.name) return product.packaging.name === 'Packaging';
      if (typeof product?.packaging === 'boolean') return product.packaging;
      return false;
    }
    return true; // optimistic default; corrected by effect below
  });

  useEffect(() => {
    if (productData?.isEdit === '1') return;
    try {
      const userCountry = preferences?.countryCode?.trim().toUpperCase();
      const rawScope = product?.market_scope ?? productData?.product?.market_scope;
      const scopeArray = parseMarketScope(rawScope);
      const shouldShow =
        (scopeArray.length > 0 && scopeArray.includes('IL') && userCountry === 'IL') ||
        scopeArray.length === 0;
      setShowPackagingOption(shouldShow);
      updateProductData({ showPackaging: shouldShow });
      updateProductData({ marketScope: scopeArray });
    } catch {
      setShowPackagingOption(true);
    }
  }, [product?.market_scope, productData?.product?.market_scope, preferences?.countryCode, productData?.isEdit]);

  // ─── Form hook ───────────────────────────────────────────────────────────────
  const form = usePersonalizeForm({
    product,
    productData,
    inputs,
    showPackagingOption,
    covers,
    t,
    locale,
  });

  // Correct inputPackaging once showPackagingOption resolves (non-IL users get undefined)
  useEffect(() => {
    if (form.is_edit === '1') return;
    if (!showPackagingOption) form.setInputPackaging(undefined);
  }, [showPackagingOption, form.is_edit]);

  // ─── Book document query ─────────────────────────────────────────────────────
  const { data, isLoading } = useQuery(
    ['personalize', {
      slug: product?.slug || productData?.product?.product_slug || '',
      inputs: JSON.stringify(form.customInputs),
      gender: form.childgender,
      type: 2,
    }],
    GetDocument,
    { enabled: !!form.childName }
  );

  // ─── Image price ─────────────────────────────────────────────────────────────
  const [imagePrice, setImagePrice] = useState(0);

  useEffect(() => {
    if (form.imageSelected === 'yes' && settings?.image_price) {
      const price = typeof settings.image_price.amount === 'string'
        ? parseFloat(settings.image_price.amount)
        : (settings.image_price.amount as number);
      setImagePrice(price || 0);
    } else {
      setImagePrice(0);
    }
  }, [form.imageSelected, settings]);

  // ─── Price calculation ───────────────────────────────────────────────────────
  const priceInPersonalize = usePriceCalculation({
    isEdit: form.is_edit ?? '0',
    product,
    covers,
    inputCover: form.inputCover,
    inputPackaging: form.inputPackaging,
    imagePrice,
    settings,
    showPackagingOption,
  });

  // ─── RTL in book ─────────────────────────────────────────────────────────────
  const [isProductRtlInBook, setIsProductRtlInBook] = useState(
    form.is_edit === '1'
      ? product?.is_rtl ?? productData?.isProductRtl ?? ''
      : product?.is_rtl ?? '0'
  );

  useEffect(() => {
    setIsProductRtlInBook(
      form.is_edit === '1'
        ? product?.is_rtl ?? productData?.isProductRtl
        : product?.is_rtl ?? '0'
    );
  }, [form.is_edit, product, productData]);

  // ─── Edit mode: load dedication image from S3 ────────────────────────────────
  const [loadingProductEdit, setLoadingProductEdit] = useState(false);

  useEffect(() => {
    if (form.is_edit !== '1') return;
    const storedKey = localStorage.getItem(`dedicationImageUrl-${form.uuid}`);
    if (!storedKey) return;
    setLoadingProductEdit(true);
    GetDedicationImage(storedKey)
      .then(async (response) => {
        const blob = await fetch(response.data.url).then((r) => r.blob());
        const file = new File([blob], 'dedication.jpg', { type: blob.type });
        form.setCroppedImage(response.data.url);
        form.setCroppedFile(file);
      })
      .catch(() => {
        form.setCroppedImage(null);
        form.setImageSelected('no');
      })
      .finally(() => setLoadingProductEdit(false));
  }, [form.is_edit, form.uuid]);

  // ─── imageSelected cleanup (edit mode) ──────────────────────────────────────
  useEffect(() => {
    if (form.is_edit === '1' && form.imageSelected === 'no') {
      localStorage.removeItem(`dedicationImageUrl-${form.uuid}`);
    }
  }, [form.imageSelected, form.is_edit, form.uuid]);

  // ─── Dedication placeholder replace ─────────────────────────────────────────
  useEffect(() => {
    if (!form.inputDedication.includes('{basmti}')) return;
    const replaceName =
      form.is_edit === '1' && product?.name
        ? product.name
        : form.childName ||
          (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) ||
          '';
    const updated = form.inputDedication.replace('{basmti}', replaceName);
    form.setInputDedication(updated);
    updateProductData({ dedication: updated });
    updateDedicationImage(form.croppedImage);
  }, [form.inputDedication, form.childName]);

  // ─── Book view ───────────────────────────────────────────────────────────────
  const [bookView, setBookview] = useState<'single' | 'double'>('single');
  const [bookViewMenu, setBookViewMenu] = useState(false);
  const desktopRef = useRef<any>();
  const [counter, setCounter] = useState(0);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCounter((c) => c + 1);
      setPage(
        (desktopRef?.current?.pageFlip()?.getPageCount() ?? 0) -
        (desktopRef?.current?.pageFlip()?.getCurrentPageIndex() ?? 0)
      );
    }, 500);
    return () => clearInterval(id);
  }, []);

  // ─── Add-to-cart loading ─────────────────────────────────────────────────────
  const [addToCartLoading, setAddToCartLoading] = useState(false);

  // ─── Cover type ──────────────────────────────────────────────────────────────
  const [coverType, setCoverType] = useState('');

  // ─── Derived dedication display ──────────────────────────────────────────────
  const hasPage1Dedication = data?.pages_dedication_texts?.some((x: any) => x.page === '1');
  const hasPage2Dedication = data?.pages_dedication_texts?.some((x: any) => x.page === '2');
  const hasOnlyOneDedicationPage =
    (hasPage1Dedication && !hasPage2Dedication) || (!hasPage1Dedication && hasPage2Dedication);
  const effectiveImageSelected = hasOnlyOneDedicationPage ? 'no' : form.imageSelected;

  // ─── Saved data (sync read) ──────────────────────────────────────────────────
  const savedData = loadPersonalizationArray();

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handleNextStep = () => {
    if (!form.childName || form.childName === '') {
      toast.custom(
        (tEl) => <CustomToast type="error" t={tEl} message={t('enterChildNameNotebook')} />,
        { duration: 2500 }
      );
      return;
    }
    if (form.childgender === '') {
      toast.custom(
        (tEl) => <CustomToast type="error" t={tEl} message={t('enterChildGenderError')} />,
        { duration: 2500 }
      );
      return;
    }
    form.markOptionCompleted('name', true);
    form.markOptionCompleted('gender', true);
    form.markOptionCompleted('child', true);
    form.setStep('review');
  };

  const handleSave = () => {
    if (form.activeOption === 'dedication') {
      if (form.inputDedication.trim() === '') {
        toast.custom(
          (tEl) => <CustomToast type="error" t={tEl} message={t('emptyDedicationError')} />,
          { duration: 2500 }
        );
        return;
      }
      if (form.imageSelected === 'yes' && !form.croppedImage) {
        toast.custom(
          (tEl) => <CustomToast type="error" t={tEl} message={t('pictureSelectionError')} />,
          { duration: 2500 }
        );
        return;
      }
      if (form.imageSelected === 'yes') updateDedicationImage(form.croppedImage);
      form.markOptionCompleted('dedication', true);
    }
    if (form.activeOption === 'cover') {
      if (!form.inputCover) {
        toast.custom(
          (tEl) => <CustomToast type="error" t={tEl} message={t('coverSelectionError')} />,
          { duration: 2500 }
        );
        return;
      }
      form.markOptionCompleted('cover', true);
    }
    if (form.activeOption === 'packaging' && showPackagingOption) {
      form.markOptionCompleted('packaging', true);
    }
    form.setStep('review');
  };

  const addToCart = async () => {
    setAddToCartLoading(true);
    setProductRtl(isProductRtlInBook);

    try {
      const selectedCover = covers?.find((x: any) => x.id == form.inputCover);

      let selectedPackagingPrice = 0;
      let packagingName = 'No-Packaging';

      if (showPackagingOption) {
        if (form.is_edit === '1' && product?.packaging?.name) {
          packagingName = product.packaging.name;
          selectedPackagingPrice = parseFloat(product.packaging.price || '0');
        } else if (form.inputPackaging === true) {
          packagingName = 'Packaging';
          selectedPackagingPrice = parseFloat(settings?.packaging_price || '0');
        }
      }

      const productBasePrice =
        form.is_edit === '1'
          ? parseFloat(product.price || product.fron_price?.amount || '0')
          : parseFloat(
              product?.price?.amount || product?.front_price?.amount || product?.price_ils || '0'
            );

      const itemDemoName =
        product?.demo_name ?? product?.product?.demo_name ?? productData?.product?.demo_name ?? '';
      const itemSlug = product?.slug ?? product?.product_slug ?? productData?.product?.product_slug;
      const itemImage = product?.images?.[0] ?? product?.image ?? product?.product?.image;

      // Handle dedication image upload
      let finalKey: string | undefined;
      if (form.imageSelected === 'yes') {
        try {
          const { data: uploadData } = await GetDedicationImageURL(itemSlug);
          const { uploadUrl, uploadedKey } = uploadData ?? {};
          if (!uploadUrl || !uploadedKey) throw new Error('Missing uploadUrl or uploadedKey.');
          await axios.put(uploadUrl, form.croppedFile, {
            headers: { 'Content-Type': form.croppedFile?.type || 'application/octet-stream' },
          });
          finalKey = uploadedKey;
          localStorage.setItem(`dedicationImageUrl-${form.uuid}`, uploadedKey);
        } catch (err) {
          console.error('Image upload failed:', err);
        }
      } else {
        const key = `dedicationImageUrl-${form.uuid}`;
        if (localStorage.getItem(key)) localStorage.removeItem(key);
      }

      const item = {
        product_id: product?.product_id || product?.id,
        uuid: form.uuid,
        replace_name: form.childName,
        inputs: { name: form.childName, age: form.inputAge },
        product_name: locale === 'ar' ? product?.product_name?.ar : product?.product_name?.en,
        demo_name: itemDemoName,
        cover_id: selectedCover?.id,
        default_replace_name:
          locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
        image: itemImage,
        product_slug: itemSlug,
        price: productBasePrice,
        quantity: 1,
        total: priceInPersonalize,
        product_type: 1 as const,
        discount: 0,
        gender: form.childgender,
        dedication_image: finalKey,
        dedication: form.inputDedication,
        packaging:
          (form.is_edit === '1' && product?.packaging) || showPackagingOption
            ? packagingName === 'Packaging'
              ? { name: 'Packaging', price: selectedPackagingPrice }
              : { name: 'No-Packaging', price: 0 }
            : undefined,
      };

      if (form.is_edit === '1') {
        cartDispatch(updateItem(item));
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('cart_updated')} />);
      } else {
        cartDispatch(addItem(item));
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('cart_added')} />);
      }

      // Persist personalization for future re-use
      savePersonalization({
        childName: form.childName,
        inputAge: form.inputAge,
        childgender: form.childgender,
        inputDedication: form.inputDedication,
        inputCover: form.inputCover,
        inputPackaging: showPackagingOption ? form.inputPackaging : undefined,
        imageSelected: form.imageSelected,
        croppedImageBase64: form.croppedImage ?? undefined,
        isFirstProductAdded: true,
      });

      // Analytics
      const productNameStr = String(item.product_name ?? '');
      event('add_to_cart', {
        currency: userCurrency,
        items: [{
          item_id: item.product_id,
          item_name: productNameStr,
          item_variant: selectedCover?.name ? String(selectedCover.name) : undefined,
          price: priceInPersonalize,
          quantity: 1,
        }],
      });
      fbq.event('AddToCart', {
        content_name: productNameStr,
        currency: userCurrency,
        value: priceInPersonalize,
      });

      router.push(`/products?name=${form.childName}`);
      setPersonalizeModal(false);
    } catch (err) {
      console.error('Error in addToCart:', err);
    } finally {
      setAddToCartLoading(false);
      form.setConfirmCart(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-white z-[140] flex flex-col text-right h-full"
        id="personalize-modal"
        key="modal"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        {/* Add to cart confirmation */}
        <ConfirmationModal
          confirmCart={form.confirmCart}
          is_edit={form.is_edit}
          addToCart={addToCart}
          addToCartLoading={addToCartLoading}
          setConfirmCart={form.setConfirmCart}
          childName={form.childName}
          product={product}
        />

        {loadingProductEdit && <GlobalLoader heading={String(t('productLoadingText'))} />}
        {form.loadDataLoading && <GlobalLoader heading={String(t('dataImportLoadingText'))} />}
        {addToCartLoading && form.is_edit === '1' && (
          <GlobalLoader heading={String(t('addToCartLoadingText'))} />
        )}

        {/* Book view menu */}
        <BookViewMenu
          step={form.step}
          bookViewMenu={bookViewMenu}
          bookView={bookView}
          setBookview={setBookview}
          setBookViewMenu={setBookViewMenu}
        />

        {/* Desktop modals */}
        <PersonalizeModals
          dedicationModal={form.dedicationModal}
          packageModal={form.packageModal}
          coverModal={form.coverModal}
          nameModal={form.nameModal}
          isSettingsLoading={isSettingsLoading}
          productData={productData}
          inputPackaging={form.inputPackaging}
          setInputPackaging={form.setInputPackaging}
          is_edit={form.is_edit}
          setPackageModal={form.setPackageModal}
          updateProductData={updateProductData}
          handleSave={handleSave}
          settings={settings}
          inputCover={form.inputCover}
          setInputCover={form.setInputCover}
          setCoverType={setCoverType}
          setCoverModal={form.setCoverModal}
          markOptionCompleted={form.markOptionCompleted}
          setInputDedication={form.setInputDedication}
          inputDedication={form.inputDedication}
          setOption={form.setOption}
          childgender={form.childgender}
          product={product}
          setDedicationModal={form.setDedicationModal}
          nameFromPersonalize={form.childName}
          croppedImage={form.croppedImage}
          setCroppedImage={form.setCroppedImage}
          imageSelected={form.imageSelected}
          setImageSelected={form.setImageSelected}
          updateDedicationImage={updateDedicationImage}
          croppedFile={form.croppedFile}
          setCroppedFile={form.setCroppedFile}
          imageInEdit={form.imageInEdit}
          inputValues={form.inputValues}
          UpdateValue={form.updateValue}
          setChildName={form.setChildName}
          setInputAge={form.setInputAge}
          inputs={inputs}
          setNameModal={form.setNameModal}
          childName={form.childName}
          inputAge={form.inputAge}
          showPackagingOption={showPackagingOption}
          userCurrency={userCurrency}
        />

        {/* Header */}
        <PersonalizeHeader
          step={form.step}
          productData={productData}
          childName={form.childName}
          setPersonalizeModal={setPersonalizeModal}
          setStep={form.setStep}
          setActiveOption={form.setActiveOption}
        />

        {/* Main content */}
        <div className="flex-1 overflow-hidden h-full">
          {form.step === 'review' && (
            <ReviewStep
              isLoading={isLoading}
              data={data}
              inputDedication={form.inputDedication}
              product={product}
              productData={productData}
              isEdit={form.is_edit}
              childName={form.childName}
              name={form.childName}
              dedicationImage={form.croppedImage}
              imageSelected={form.imageSelected}
              bookView={bookView}
              isProductRtlInBook={isProductRtlInBook}
              desktopRef={desktopRef}
              setStep={form.setStep}
              setActiveOption={form.setActiveOption}
              setDedicationModal={form.setDedicationModal}
            />
          )}

          {(form.step === 'info' || form.step === 'edit') &&
            (form.activeOption === 'name' ||
              form.activeOption === 'gender' ||
              form.activeOption === 'age' ||
              form.activeOption === 'init' ||
              form.activeOption === 'first_name' ||
              form.activeOption === 'child') && (
              <InfoStep
                inputValues={form.inputValues}
                inputs={inputs}
                is_edit={form.is_edit}
                UpdateValue={form.updateValue}
                setChildName={form.setChildName}
                setInputAge={form.setInputAge}
                step={form.step}
                updateProductData={updateProductData}
                handleNextStep={handleNextStep}
                setPersonalizeModal={setPersonalizeModal}
                childName={form.childName}
                inputAge={form.inputAge}
                childgender={form.childgender}
                savedData={savedData}
                setLoadData={form.setLoadData}
                loadDataLoading={form.loadDataLoading}
                user={user}
                preferences={preferences}
                showSavedData={form.showSavedData}
                setShowSavedData={form.setShowSavedData}
                isPostLoginFlow={form.isPostLoginFlow}
                handleAddNew={form.handleAddNew}
                disableSavedData={form.hasValidUrlName}
              />
            )}

          {form.step === 'edit' && form.activeOption === 'dedication' && (
            <EditStep
              childgender={form.childgender}
              product={product}
              setInputDedication={form.setInputDedication}
              inputDedication={form.inputDedication}
              setOption={form.setOption}
              nameFromPersonalize={form.childName}
              croppedImage={form.croppedImage}
              setCroppedImage={form.setCroppedImage}
              imageSelected={form.imageSelected}
              setImageSelected={form.setImageSelected}
              updateDedicationImage={updateDedicationImage}
              effectiveImageSelected={effectiveImageSelected}
              croppedFile={form.croppedFile}
              setCroppedFile={form.setCroppedFile}
              imageInEdit={form.imageInEdit}
            />
          )}

          {form.step === 'edit' && form.activeOption === 'cover' && (
            <div
              className="flex-1 overflow-y-auto p-4 bg-[#FFFCEB] min-h-full"
              dir={locale === 'ar' ? 'rtl' : 'ltr'}
            >
              <CoversSelection
                product={productData?.product}
                inputCover={form.inputCover}
                setInputCover={form.setInputCover}
                setCoverType={setCoverType}
                isEdit={form.is_edit}
                userCurrency={userCurrency}
                updateProductData={updateProductData}
                handleSave={handleSave}
                markOptionCompleted={form.markOptionCompleted}
                setCoverModal={form.setCoverModal}
              />
            </div>
          )}

          {form.step === 'edit' &&
            form.activeOption === 'packaging' &&
            ((form.is_edit === '1' && product?.packaging) || showPackagingOption) && (
              <div
                className="flex-1 overflow-y-auto p-4 bg-[#FFFCEB] min-h-full"
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
              >
                {!isSettingsLoading ? (
                  <PackagingSelection
                    product={productData?.product}
                    inputPackaging={form.inputPackaging}
                    setInputPackaging={form.setInputPackaging}
                    isEdit={form.is_edit}
                    updateProductData={updateProductData}
                    handleSave={handleSave}
                    markCompleted={form.markOptionCompleted}
                    packagingPrice={settings?.packaging_price}
                  />
                ) : (
                  <p>{t('packagingLoadingText')}</p>
                )}
              </div>
            )}
        </div>

        {/* Footer */}
        <PersonalizationFooter
          step={form.step}
          activeOption={form.activeOption}
          options={form.options}
          childName={form.childName}
          inputAge={form.inputAge}
          childgender={form.childgender}
          inputDedication={form.inputDedication}
          inputCover={form.inputCover}
          inputPackaging={form.inputPackaging}
          is_edit={form.is_edit}
          priceInPersonalize={priceInPersonalize}
          priceSymbol={priceSymbol}
          inputs={inputs}
          inputValues={form.inputValues}
          settings={settings}
          setStep={form.setStep}
          setActiveOption={form.setActiveOption}
          setNameModal={form.setNameModal}
          setDedicationModal={form.setDedicationModal}
          setCoverModal={form.setCoverModal}
          setPackageModal={form.setPackageModal}
          setConfirmCart={form.setConfirmCart}
          setPersonalizeModal={setPersonalizeModal}
          updateProductData={updateProductData}
          handleNextStep={handleNextStep}
          handleSave={handleSave}
          addToCart={addToCart}
          showPackagingOption={showPackagingOption}
        />
      </motion.div>
    </AnimatePresence>
  );
}
